import { AuthXInterceptor } from '../Config/AuthXInterceptor'
import {
  CreateFacilityPayload,
  UpdateConfigPayload,
  LimitFacilityPayload,
  BoostCreditUserDetails,
  BoostCreditDrawdownsResponse,
  DirectDebitAttemptsResponse,
  FacilityRepaymentsResponse,
  DirectDebitBalanceResponse,
} from 'BoostXpressAdmin/Model/boostCreditXModel'

export const boostCreditBXService = {
  // Get all boost credit facilities
  index: async (params?: {
    page?: number
    limit?: number
    sortOrder?: 'ASC' | 'DESC'
    status?: string
    userResourceId?: string
  }) => {
    return await AuthXInterceptor.get('boost-credit/facilities', {
      params,
    })
  },

  // Get boost credit config
  getConfig: async () => {
    return await AuthXInterceptor.get('boost-credit/config')
  },

  // Update boost credit config
  updateConfig: async (payload: UpdateConfigPayload) => {
    return await AuthXInterceptor.put('boost-credit/config', payload)
  },

  // Create a new facility (dev endpoint)
  createFacility: async (payload: CreateFacilityPayload) => {
    return await AuthXInterceptor.post('boost-credit/facilities/dev', payload)
  },

  // Suspend a facility
  suspendFacility: async (facilityId: string) => {
    return await AuthXInterceptor.put(`boost-credit/facilities/${facilityId}/suspend`)
  },

  // Reinstate a facility
  reinstateFacility: async (facilityId: string) => {
    return await AuthXInterceptor.put(`boost-credit/facilities/${facilityId}/reinstate`)
  },

  // Limit a facility
  limitFacility: async (facilityId: string, payload: LimitFacilityPayload) => {
    return await AuthXInterceptor.put(`boost-credit/facilities/${facilityId}/limit`, payload)
  },

  // Get user boost credit details
  getUserDetails: async (userResourceId: string): Promise<{ data: { data: BoostCreditUserDetails } }> => {
    return await AuthXInterceptor.get(`boost-credit/users/${userResourceId}`)
  },

  // Get user drawdowns (usage history)
  getDrawdowns: async (params: {
    page?: number
    limit?: number
    sortOrder?: 'ASC' | 'DESC'
    userResourceId: string
    status?: string
    coverage?: string
    hasOutstandingBalance?: boolean
  }): Promise<{data:BoostCreditDrawdownsResponse}> => {
    return await AuthXInterceptor.get('boost-credit/drawdowns', { params })
  },

  // Get user facilities list
  getUserFacilities: async (params: {
    page?: number
    limit?: number
    sortOrder?: 'ASC' | 'DESC'
    userResourceId: string
    status?: string
  }) => {
    return await AuthXInterceptor.get('boost-credit/facilities', { params })
  },

  // Get direct debit attempts for a facility
  getDirectDebitAttempts: async (facilityResourceId: string, params?: {
    page?: number
    limit?: number
  }): Promise<{data:DirectDebitAttemptsResponse}> => {
    return await AuthXInterceptor.get(`boost-credit/facilities/${facilityResourceId}/direct-debit/attempts`, { params })
  },

  // Get facility repayments
  getFacilityRepayments: async (facilityResourceId: string, params?: {
    page?: number
    limit?: number
    source?: string
  }): Promise<{data:FacilityRepaymentsResponse}> => {
    return await AuthXInterceptor.get(`boost-credit/facilities/${facilityResourceId}/repayments`, { params })
  },

  // Reconcile direct debit attempt
  reconcileDirectDebitAttempt: async (attemptResourceId: string) => {
    return await AuthXInterceptor.post(`boost-credit/direct-debit/attempts/${attemptResourceId}/reconcile`)
  },

  // Trigger direct debit on a facility
  triggerDirectDebit: async (facilityResourceId: string, amountMajor: number) => {
    return await AuthXInterceptor.post(`boost-credit/facilities/${facilityResourceId}/direct-debit/debit`, {
      amountMajor,
    })
  },

  // Check direct debit balance for a facility
  checkDirectDebitBalance: async (facilityResourceId: string): Promise<DirectDebitBalanceResponse> => {
    return await AuthXInterceptor.get(`boost-credit/facilities/${facilityResourceId}/direct-debit/balance`)
  },
}
